/**
 * Status badges (ADR 0005): a small row of tags above each unit while a
 * status, a stage change or a charge lasts, from the state after each
 * command (`unit.statuses`, `unit.stages`, `unit.charging`). The row follows
 * its unit on screen: `locate` (the renderer's projection, through the shell)
 * is read once per animation frame while any badge is shown. Tag text from
 * `ui.badge.*` keys; a stage shows its stat with an up or down chevron.
 */
import { useEffect, useRef } from 'react';
import type { StageStat, Status } from '@ab/game-core';
import type { Strings } from '@ab/session';
import { formatText, text } from './text.ts';
import './overlays.css';

export type BadgeRow = {
  readonly unit: string;
  readonly statuses: readonly Status[];
  /** Each stat's total stage, only the non-zero ones. */
  readonly stages: readonly { readonly stat: StageStat; readonly stages: number }[];
  readonly charging: boolean;
};

export type StatusBadgesProps = {
  readonly rows: readonly BadgeRow[];
  readonly strings: Strings;
  /** Where the unit's head is now, CSS pixels; null or not visible hides its row. */
  readonly locate: (unit: string) => { readonly x: number; readonly y: number; readonly visible: boolean } | null;
};

export const StatusBadges = ({ rows, strings, locate }: StatusBadgesProps) => {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (rows.length === 0) return;
    let frame = 0;
    const place = (): void => {
      for (const el of root.current?.querySelectorAll<HTMLElement>('[data-unit]') ?? []) {
        const at = locate(el.dataset['unit'] ?? '');
        el.hidden = at === null || !at.visible;
        if (at !== null) el.style.transform = `translate(${at.x.toFixed(1)}px, ${at.y.toFixed(1)}px) translate(-50%, -100%)`;
      }
      frame = requestAnimationFrame(place);
    };
    place();
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [rows, locate]);
  return (
    <div className="ab-badges" ref={root} data-testid="status-badges">
      {rows.map((row) => (
        <div key={row.unit} className="ab-badge-row" data-unit={row.unit} hidden>
          {row.statuses.map((s) => (
            <span key={s} className="ab-badge ab-badge-status">
              {text(strings, `ui.badge.${s}`)}
            </span>
          ))}
          {row.stages.map((s) => (
            <span key={s.stat} className={`ab-badge ${s.stages > 0 ? 'ab-badge-up' : 'ab-badge-down'}`}>
              {formatText(strings, s.stages > 0 ? 'ui.badge.stage_up' : 'ui.badge.stage_down', { keys: { stat: `ui.badge.stat_${s.stat}` } })}
            </span>
          ))}
          {row.charging && <span className="ab-badge ab-badge-charge">{text(strings, 'ui.badge.charging')}</span>}
        </div>
      ))}
    </div>
  );
};
